import { inject, Injectable } from '@angular/core';
import { EjournalApiService } from '@et/api';
import { NotificationService } from '@et/notifications';
import { EtjournalPacketEntry } from '@et/typings';
import { Store } from '@ngxs/store';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { SetEjournal } from '../state/ejournal-session.actions';
import { EjournalSessionSelectors } from '../state/ejournal-session.selectors';
import { EjournalEntriesService } from './ejournal-entries.service';
import { EjournalSessionCancelService } from './ejournal-session-cancel.service';

@Injectable()
export class EjournalFinalizeService {
  private readonly ejournalApiService = inject(EjournalApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly entriesService = inject(EjournalEntriesService);
  private readonly sessionCancel = inject(EjournalSessionCancelService);
  private readonly store = inject(Store);

  private readonly eJournal = this.store.selectSignal(
    EjournalSessionSelectors.getEjournal,
  );
  private readonly selectedPacket = this.store.selectSignal(
    EjournalSessionSelectors.getSelectedPacket,
  );
  private readonly sealEntries = this.store.selectSignal(
    EjournalSessionSelectors.getSealEntries,
  );

  /**
   * Finalizes an eJournal.
   * Deletes seal holder placeholders first, then backfills `utcTimeOfSigning`
   * from the selected packet's `utcDateSigned` when the journal was created
   * before signing.
   */
  finalizeEJournal() {
    const eJournal = this.eJournal()!;
    const ensureSigningTime$ = eJournal.utcTimeOfSigning
      ? of(eJournal)
      : this.backfillUtcTimeOfSigning(eJournal);

    return this.deletePlaceholderEntries().pipe(
      switchMap(() => ensureSigningTime$),
      switchMap((journal) =>
        this.ejournalApiService.finalizeEjournal(journal.id).pipe(
          tap(() => {
            this.notificationService.showSuccess(
              'EJournal finalized successfully',
            );
            this.store.dispatch(new SetEjournal({ ...journal, isFinal: true }));
          }),
        ),
      ),
      this.sessionCancel.takeUntilCancel(),
      catchError((error) => {
        this.notificationService.showError('Failed to finalize eJournal');
        throw error;
      }),
    );
  }

  /**
   * Deletes unassigned seal holder placeholder entries before finalization.
   */
  private deletePlaceholderEntries() {
    const placeholders = this.sealEntries();
    if (placeholders.length === 0) {
      return of(null);
    }
    const ids = placeholders.map((e) => e.eJournalEntryId);
    return this.ejournalApiService
      .deleteEntries(ids)
      .pipe(tap(() => this.entriesService.removeEntriesFromLocalState(ids)));
  }

  /**
   * Patches the journal packet with `utcTimeOfSigning` derived from
   * `selectedPacket.utcDateSigned` (same conversion as journal-form create).
   */
  private backfillUtcTimeOfSigning(eJournal: EtjournalPacketEntry) {
    const utcDateSigned = this.selectedPacket()?.utcDateSigned;
    if (!utcDateSigned) {
      return of(eJournal);
    }

    const utcTimeOfSigning = new Date(utcDateSigned + 'Z').toISOString();
    return this.ejournalApiService
      .patchPacketJournal(eJournal.id, { utcTimeOfSigning })
      .pipe(
        map((updated) => ({
          ...eJournal,
          ...updated,
          utcTimeOfSigning: updated.utcTimeOfSigning ?? utcTimeOfSigning,
        })),
      );
  }
}
